import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  ADMIN_CREATOR_FILTERS,
  ADMIN_CREATORS_DEFAULT_PAGE_SIZE,
  CREATOR_STATUS_TRANSITIONS,
  creatorAwaitsDecision,
  PLATFORM_LABELS,
  PROOF_METHOD_LABELS,
  type AdminCreatorFilter,
  type Connection,
  type CreatorStatus,
} from '@nexus/shared';

import { ApiError, apiGet, apiSend } from '../../lib/api';
import {
  Alert,
  Badge,
  Button,
  Card,
  ConfirmButton,
  EmptyState,
  Fact,
  Facts,
  LoadingPage,
  Modal,
  Num,
  PageHeader,
  Pill,
  Pills,
  SearchField,
  Stat,
  StatGrid,
  StatusPill,
  TextField,
  When,
  statusLabel,
  statusTone,
} from '../../ui';

export interface CreatorRow {
  id: string;
  userId: string;
  displayName: string | null;
  country: string | null;
  status: CreatorStatus;
  statusReason: string | null;
  reviewedAt: string | null;
  createdAt: string;
  email: string;
  emailVerifiedAt: string | null;
  userStatus: string;
  lastLoginAt: string | null;
  acceptedTerms: { version: string; acceptedAt: string; documentHash: string } | null;
  connectedAccounts: number;
  connections: Connection[];
}

export interface CreatorListResponse {
  creators: CreatorRow[];
  page: number;
  pageSize: number;
  total: number;
  filter: AdminCreatorFilter;
  q: string;
  counts: {
    total: number;
    needsReview: number;
    approved: number;
    suspended: number;
    rejected: number;
    unconfirmed: number;
    noConnection: number;
  };
  autoApprove: boolean;
}

/**
 * Creators, their connections, and the evidence behind each one.
 *
 * This is the page that answers "how do you know that account is theirs" — so
 * it shows the actual check rows and their evidence JSON, not a green tick.
 * The evidence is what a payment dispute is settled with, and a screen that
 * summarises it away is a screen that cannot settle one.
 *
 * FOR A HUNDRED PEOPLE, NOT TEN. The list is paged and filtered by the API
 * (`GET /admin/creators?filter=&q=&page=`); this page only asks. The filter,
 * the search and the page live in the URL, so the Overview can link straight
 * to "everybody who has not confirmed their email" and the operator can send
 * a colleague the same view. `NEWEST` is the default: with auto-approval on
 * there is no queue to lead with, and the question on launch day is "who
 * arrived". `NEEDS_REVIEW` is still there for the manual mode, and the API
 * says which mode it is in so the heading can lead with the queue when there
 * is one.
 */
const FILTER_LABELS: Record<AdminCreatorFilter, string> = {
  NEWEST: 'Newest',
  NEEDS_REVIEW: 'Needs review',
  APPROVED: 'Approved',
  SUSPENDED: 'Suspended',
  REJECTED: 'Rejected',
  UNCONFIRMED: 'Unconfirmed email',
  NO_CONNECTION: 'No proven account',
};

function isFilter(value: string | null): value is AdminCreatorFilter {
  return (ADMIN_CREATOR_FILTERS as readonly string[]).includes(value ?? '');
}

export function AdminCreators(): React.JSX.Element {
  const [params, setParams] = useSearchParams();
  const rawFilter = params.get('filter');
  const filter: AdminCreatorFilter = isFilter(rawFilter) ? rawFilter : 'NEWEST';
  const q = params.get('q') ?? '';
  const page = Math.max(1, Number(params.get('page') ?? '1') || 1);

  const [typed, setTyped] = useState(q);
  const [data, setData] = useState<CreatorListResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkNote, setBulkNote] = useState<string | null>(null);

  const setQuery = useCallback(
    (next: { filter?: AdminCreatorFilter; q?: string; page?: number }) => {
      const out = new URLSearchParams(params);
      const f = next.filter ?? filter;
      const term = next.q ?? q;
      const p = next.page ?? 1;
      if (f === 'NEWEST') out.delete('filter');
      else out.set('filter', f);
      if (term.trim()) out.set('q', term.trim());
      else out.delete('q');
      if (p > 1) out.set('page', String(p));
      else out.delete('page');
      setParams(out, { replace: true });
    },
    [params, setParams, filter, q],
  );

  /* The search box writes to the URL after a pause, so a request is not made
     per keystroke and Back still works. */
  useEffect(() => {
    if (typed === q) return;
    const timer = window.setTimeout(() => setQuery({ q: typed, page: 1 }), 300);
    return () => window.clearTimeout(timer);
  }, [typed, q, setQuery]);

  const load = useCallback(async () => {
    const search = new URLSearchParams();
    search.set('filter', filter);
    if (q) search.set('q', q);
    search.set('page', String(page));
    search.set('pageSize', String(ADMIN_CREATORS_DEFAULT_PAGE_SIZE));
    try {
      const body = await apiGet<CreatorListResponse>(`/admin/creators?${search.toString()}`);
      setData(body);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load creators.');
    }
  }, [filter, q, page]);

  useEffect(() => {
    void load();
  }, [load]);

  const counts = data?.counts;
  const filterCount = useMemo(() => {
    if (!counts) return (): number | undefined => undefined;
    const map: Record<AdminCreatorFilter, number> = {
      NEWEST: counts.total,
      NEEDS_REVIEW: counts.needsReview,
      APPROVED: counts.approved,
      SUSPENDED: counts.suspended,
      REJECTED: counts.rejected,
      UNCONFIRMED: counts.unconfirmed,
      NO_CONNECTION: counts.noConnection,
    };
    return (f: AdminCreatorFilter): number => map[f];
  }, [counts]);

  async function bulkApprove(): Promise<void> {
    const ids = [...selected];
    if (ids.length === 0) return;
    setBulkBusy(true);
    setBulkNote(null);
    try {
      const body = await apiSend<{ approved: string[]; skipped: string[] }>(
        'POST',
        '/admin/creators/bulk-approve',
        { ids },
      );
      setBulkNote(
        `Approved ${body.approved.length}.${body.skipped.length > 0 ? ` ${body.skipped.length} had already been decided and were left alone.` : ''}`,
      );
      setSelected(new Set());
      await load();
    } catch (err) {
      setBulkNote(err instanceof ApiError ? err.message : 'Could not approve them.');
    } finally {
      setBulkBusy(false);
    }
  }

  if (error && data === null) {
    return (
      <>
        <PageHeader eyebrow="Operator" title="Creators" />
        <Alert tone="critical" title="Could not load creators.">
          <span>{error}</span>
        </Alert>
      </>
    );
  }

  if (data === null) return <LoadingPage eyebrow="Operator" title="Creators" stats />;

  const waiting = data.counts.needsReview;
  const manual = !data.autoApprove;
  const pageCount = Math.max(1, Math.ceil(data.total / data.pageSize));
  const first = data.total === 0 ? 0 : (data.page - 1) * data.pageSize + 1;
  const last = Math.min(data.total, data.page * data.pageSize);
  const selectable = data.creators.filter((c) => creatorAwaitsDecision(c.status));

  return (
    <>
      <PageHeader
        eyebrow="Operator"
        title={manual && waiting > 0 ? `Creators · ${waiting} waiting for approval` : 'Creators'}
        lead={
          manual
            ? 'Nobody can join a campaign until you approve them, so those come first. Each connection shows how ownership was proven and what the check actually saw.'
            : 'A confirmed email approves a creator automatically; you can still suspend or reject anybody. Each connection shows how ownership was proven and what the check actually saw — which is what a payment dispute is settled with.'
        }
        actions={
          manual && waiting > 0 && filter !== 'NEEDS_REVIEW' ? (
            <Button variant="primary" onClick={() => setQuery({ filter: 'NEEDS_REVIEW', page: 1 })}>
              Show the {waiting} waiting
            </Button>
          ) : undefined
        }
      />

      <StatGrid>
        <Stat label="Creators" value={<Num value={data.counts.total} />} />
        <Stat
          label="Unconfirmed email"
          value={<Num value={data.counts.unconfirmed} />}
          note="Cannot submit or be paid until they confirm."
          emphasis={data.counts.unconfirmed > 0}
        />
        <Stat
          label="No proven account"
          value={<Num value={data.counts.noConnection} />}
          note="Signed up, nothing connected yet."
        />
        {(manual || waiting > 0) && (
          <Stat
            label="Waiting for approval"
            value={<Num value={waiting} />}
            note={waiting > 0 ? 'Not approved yet.' : 'Nobody is waiting.'}
            emphasis={waiting > 0}
          />
        )}
      </StatGrid>

      <Card tight>
        <SearchField
          label="Search creators"
          placeholder="Name, email or @handle"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          hint={q ? `Showing matches for “${q}”.` : 'Name, email address or a connected handle.'}
        />
        <Pills label="Show">
          {ADMIN_CREATOR_FILTERS.map((f) => (
            <Pill
              key={f}
              selected={filter === f}
              count={filterCount(f)}
              onClick={() => setQuery({ filter: f, page: 1 })}
            >
              {FILTER_LABELS[f]}
            </Pill>
          ))}
        </Pills>
      </Card>

      {selectable.length > 0 && (
        <div className="actions">
          <Button
            small
            onClick={() =>
              setSelected((s) =>
                s.size === selectable.length ? new Set() : new Set(selectable.map((c) => c.id)),
              )
            }
          >
            {selected.size === selectable.length ? 'Select none' : `Select all ${selectable.length} waiting on this page`}
          </Button>
          <ConfirmButton
            variant="primary"
            label={`Approve selected (${selected.size})`}
            title={`Approve ${selected.size} creator${selected.size === 1 ? '' : 's'}?`}
            question="They can join campaigns and submit clips from the moment you confirm. Nothing is sent to them and no reason is recorded — an approval is the absence of a problem."
            confirmLabel="Approve them"
            disabled={bulkBusy || selected.size === 0}
            onConfirm={() => void bulkApprove()}
          />
          {bulkNote && <span className="muted">{bulkNote}</span>}
        </div>
      )}

      {data.counts.total === 0 ? (
        <Card>
          <EmptyState title="No creators yet.">
            The first signup appears here. Signup is open at {window.location.origin}/signup.
          </EmptyState>
        </Card>
      ) : data.creators.length === 0 ? (
        <Card>
          <EmptyState
            title={
              q
                ? `Nobody matches “${q}”.`
                : filter === 'NEEDS_REVIEW'
                  ? 'Nobody is waiting for approval.'
                  : 'No creator is in that state.'
            }
          >
            <Button
              onClick={() => {
                setTyped('');
                setQuery({ filter: 'NEWEST', q: '', page: 1 });
              }}
            >
              Show everyone
            </Button>
          </EmptyState>
        </Card>
      ) : (
        <>
          <p className="muted">
            Showing {first}–{last} of {data.total}
            {q ? ` matching “${q}”` : ''}
            {filter !== 'NEWEST' ? ` · ${FILTER_LABELS[filter]}` : ''}.
          </p>
          {data.creators.map((creator) => (
            <CreatorCard
              key={creator.id}
              creator={creator}
              onChanged={load}
              selectable={creatorAwaitsDecision(creator.status)}
              selected={selected.has(creator.id)}
              onSelect={(on) =>
                setSelected((s) => {
                  const next = new Set(s);
                  if (on) next.add(creator.id);
                  else next.delete(creator.id);
                  return next;
                })
              }
            />
          ))}
          {pageCount > 1 && (
            <nav className="actions" aria-label="Pages">
              <Button small disabled={data.page <= 1} onClick={() => setQuery({ page: data.page - 1 })}>
                Previous
              </Button>
              <span className="muted">
                Page {data.page} of {pageCount}
              </span>
              <Button
                small
                disabled={data.page >= pageCount}
                onClick={() => setQuery({ page: data.page + 1 })}
              >
                Next
              </Button>
            </nav>
          )}
        </>
      )}
    </>
  );
}

function CreatorCard({
  creator,
  onChanged,
  selectable,
  selected,
  onSelect,
}: {
  creator: CreatorRow;
  onChanged: () => Promise<void>;
  selectable: boolean;
  selected: boolean;
  onSelect: (on: boolean) => void;
}): React.JSX.Element {
  return (
    <Card
      label={creator.email}
      eyebrow={creator.email}
      title={creator.displayName ?? creator.email}
      aside={
        <span className="actions">
          {selectable && (
            <label className="check">
              <input
                type="checkbox"
                checked={selected}
                onChange={(e) => onSelect(e.target.checked)}
                aria-label={`Select ${creator.displayName ?? creator.email} for approval`}
              />
              <span>Select</span>
            </label>
          )}
          <StatusPill status={creator.status} domain="creator" />
        </span>
      }
    >
      <CreatorDecision creator={creator} onChanged={onChanged} />

      <Facts>
        <Fact label="Country">{creator.country ?? '—'}</Fact>
        <Fact label="Email confirmed">
          {creator.emailVerifiedAt ? (
            <When value={creator.emailVerifiedAt} />
          ) : (
            <Badge tone="critical">not confirmed</Badge>
          )}
        </Fact>
        <Fact label="Proven accounts">
          <Num value={creator.connectedAccounts} />
          {creator.connections.length > creator.connectedAccounts &&
            ` of ${creator.connections.length} connected`}
        </Fact>
        <Fact label="Last sign-in">
          {creator.lastLoginAt ? <When value={creator.lastLoginAt} withTime /> : 'Never'}
        </Fact>
        <Fact label="Terms">
          {creator.acceptedTerms ? (
            <>
              {creator.acceptedTerms.version} on <When value={creator.acceptedTerms.acceptedAt} />
            </>
          ) : (
            <Badge tone="critical">not accepted</Badge>
          )}
        </Fact>
        <Fact label="Joined">
          <When value={creator.createdAt} withTime />
        </Fact>
      </Facts>

      {creator.connections.length === 0 ? (
        <EmptyState title="No connections.">
          This creator cannot submit anything until they prove an account.
        </EmptyState>
      ) : (
        creator.connections.map((connection) => (
          <ConnectionBlock key={connection.id} connection={connection} onChanged={onChanged} />
        ))
      )}
    </Card>
  );
}

/**
 * Approve, reject, suspend — and the sentence behind whichever already happened.
 *
 * WHAT IS OFFERED COMES FROM THE SHARED TRANSITION MAP, not from a list typed
 * out here: the Worker refuses anything else with a 409, and a screen that
 * offers a button the API will refuse is a screen that teaches an operator to
 * distrust it. An APPROVED creator sees only Suspend; a REJECTED one sees no
 * button at all, because a rejection is a decision the person has already been
 * told about in words and undoing it silently would leave that sentence
 * standing with nothing to say what changed.
 *
 * APPROVE IS ONE CLICK AND A CONFIRMATION. REJECT AND SUSPEND ARE A MODAL WITH
 * A REQUIRED REASON, refused here before the round trip, because the creator
 * reads that sentence on their Profile and on every campaign card and "your
 * account is not approved" with no cause is a support conversation by design.
 */
function CreatorDecision({
  creator,
  onChanged,
}: {
  creator: CreatorRow;
  onChanged: () => Promise<void>;
}): React.JSX.Element | null {
  const [asking, setAsking] = useState<'REJECTED' | 'SUSPENDED' | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const allowed = CREATOR_STATUS_TRANSITIONS[creator.status];

  async function decide(status: CreatorStatus, reason?: string): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await apiSend('PATCH', `/admin/creators/${creator.id}/status`, {
        status,
        ...(reason ? { reason } : {}),
      });
      setAsking(null);
      await onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not record that.');
    } finally {
      setBusy(false);
    }
  }

  const name = creator.displayName ?? creator.email;

  return (
    <>
      {creator.statusReason && (
        <Alert
          tone={creator.status === 'APPROVED' ? 'waiting' : 'critical'}
          title={`${statusLabel(creator.status, 'creator')} — what they were told`}
        >
          <span>{creator.statusReason}</span>
          {creator.reviewedAt && (
            <span className="muted">
              Recorded <When value={creator.reviewedAt} withTime />
            </span>
          )}
        </Alert>
      )}

      {error && <Alert tone="critical">{error}</Alert>}

      {allowed.length > 0 && (
        <div className="actions">
          {allowed.includes('APPROVED') && (
            <ConfirmButton
              variant="primary"
              label="Approve"
              title={`Approve ${name}?`}
              question="They can join campaigns and submit clips from the moment you confirm. Nothing is sent to them and no reason is recorded — an approval is the absence of a problem."
              confirmLabel="Approve"
              disabled={busy}
              onConfirm={() => void decide('APPROVED')}
            />
          )}
          {allowed.includes('REJECTED') && (
            <Button variant="danger" disabled={busy} onClick={() => setAsking('REJECTED')}>
              Reject
            </Button>
          )}
          {allowed.includes('SUSPENDED') && (
            <Button variant="danger" disabled={busy} onClick={() => setAsking('SUSPENDED')}>
              Suspend
            </Button>
          )}
        </div>
      )}

      {asking && (
        <ReasonModal
          decision={asking}
          name={name}
          busy={busy}
          onClose={() => setAsking(null)}
          onConfirm={(reason) => void decide(asking, reason)}
        />
      )}
    </>
  );
}

const DECISION_COPY = {
  REJECTED: {
    title: (name: string) => `Reject ${name}?`,
    body: 'They keep their account and their connections, and they can never join a campaign. This is final: there is no un-reject, because the sentence below is what they will be reading.',
    label: 'Reject',
  },
  SUSPENDED: {
    title: (name: string) => `Suspend ${name}?`,
    body: 'They are signed out immediately and cannot join or submit anything until you approve them again. Earnings they have already made are untouched.',
    label: 'Suspend',
  },
} as const;

function ReasonModal({
  decision,
  name,
  busy,
  onClose,
  onConfirm,
}: {
  decision: 'REJECTED' | 'SUSPENDED';
  name: string;
  busy: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
}): React.JSX.Element {
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const copy = DECISION_COPY[decision];

  function submit(): void {
    /* Refused here, before the request. The API refuses it too — that is the
       guarantee — but an operator finding out after a round trip has already
       watched a spinner for nothing. */
    if (reason.trim().length < 3) {
      setError('A reason is required: the creator is shown it word for word.');
      return;
    }
    onConfirm(reason.trim());
  }

  return (
    <Modal
      title={copy.title(name)}
      onClose={onClose}
      footer={
        <Button variant="danger" disabled={busy} onClick={submit}>
          {copy.label}
        </Button>
      }
    >
      <p>{copy.body}</p>
      <TextField
        label="Reason"
        textarea
        rows={3}
        name="reason"
        value={reason}
        onChange={(e) => {
          setReason(e.target.value);
          setError(null);
        }}
        error={error}
        placeholder="What you want them to read"
        hint="Shown on their Profile and on every campaign card, exactly as typed."
      />
    </Modal>
  );
}

function ConnectionBlock({
  connection,
  onChanged,
}: {
  connection: Connection;
  onChanged: () => Promise<void>;
}): React.JSX.Element {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function revoke(): Promise<void> {
    if (reason.trim().length < 3) {
      setError('A revocation needs a reason: the creator is shown it.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await apiSend('POST', `/admin/connections/${connection.id}/revoke`, { reason });
      setReason('');
      await onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not revoke it.');
    } finally {
      setBusy(false);
    }
  }

  const live = connection.status !== 'REVOKED' && connection.status !== 'REJECTED';

  return (
    <div className="group">
      <div className="row-head">
        <span className="row-title">
          {PLATFORM_LABELS[connection.platform]} {connection.handle}
        </span>
        <Badge tone={statusTone(connection.status, 'connection')}>
          {statusLabel(connection.status, 'connection')}
        </Badge>
      </div>

      <Facts>
        <Fact label="Proven by">
          {connection.proofMethod ? PROOF_METHOD_LABELS[connection.proofMethod] : 'Unproven'}
        </Fact>
        <Fact label="Followers at connection">
          <Num value={connection.followerCountAtConnect} fallback="Not published" />
        </Fact>
        <Fact label="Platform id">
          <code className="chip quiet break">{connection.platformAccountId ?? '—'}</code>
          {connection.platformAccountIdSource && (
            <span className="muted"> ({statusLabel(connection.platformAccountIdSource)})</span>
          )}
        </Fact>
        {connection.statsAuthorId && connection.statsAuthorId !== connection.platformAccountId && (
          <Fact label="Stats id">
            {/* TikTok's OAuth open_id never matches a post read's author id, so
                the two are shown separately rather than reconciled. */}
            <code className="chip quiet break">{connection.statsAuthorId}</code>
          </Fact>
        )}
      </Facts>

      {connection.revokedReason && <Alert tone="critical">{connection.revokedReason}</Alert>}

      <details className="reveal">
        <summary>
          Evidence ({connection.checks.length} check{connection.checks.length === 1 ? '' : 's'})
        </summary>
        {connection.checks.length === 0 ? (
          <p className="muted">Nothing has been checked on this connection yet.</p>
        ) : (
          <ul className="checklist">
            {connection.checks.map((check) => (
              <li
                key={check.id}
                data-result={check.result}
                data-state={
                  check.result === 'PASS' ? 'pass' : check.result === 'FAIL' ? 'fail' : 'exception'
                }
              >
                <span className="mark" aria-hidden="true">
                  {check.result === 'PASS' ? '✓' : check.result === 'FAIL' ? '✕' : '?'}
                </span>
                <span>
                  <span className="check-label">{statusLabel(check.checkType)}</span>{' '}
                  <span className="muted">
                    <When value={check.checkedAt} withTime />
                  </span>
                  {check.note && <div className="check-note">{check.note}</div>}
                  {check.evidence != null && (
                    <pre className="prose-block evidence">
                      {JSON.stringify(check.evidence, null, 2)}
                    </pre>
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}
      </details>

      {live && (
        <>
          <TextField
            label="Revoke, with a reason"
            type="text"
            name="reason"
            minLength={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why this connection is being taken away"
            hint="The creator is shown this word for word, and every earning already made on this account keeps its evidence."
            {...(error ? { 'aria-invalid': true } : {})}
          />
          {error && <Alert tone="critical">{error}</Alert>}
          <div className="actions">
            <ConfirmButton
              label="Revoke"
              title={`Revoke ${PLATFORM_LABELS[connection.platform]} ${connection.handle}?`}
              question="The creator can no longer submit from this account. Earnings already made on it are untouched, and the connection stays on the record with your reason."
              confirmLabel="Revoke it"
              disabled={busy}
              onConfirm={() => void revoke()}
            />
          </div>
        </>
      )}
    </div>
  );
}
